/**
 * Notification Broadcast API utilities
 * Handles lists, templates, and campaign management for broadcast notifications
 */

import { API_BASE_URL } from '../config/api';

// ============================================
// Types
// ============================================

export type NotificationListType = 'email' | 'phone';
export type NotificationChannel = 'sms' | 'email_plain' | 'email_html';
export type CampaignStatus = 'pending' | 'sending' | 'completed' | 'failed';

export type DateFilterType = 'after' | 'before' | 'between';

export interface FilterCriteria {
  species?: string[];
  customer_status?: string[];
  // Date-based customer filtering
  customer_created_date_type?: DateFilterType;
  customer_created_after?: string;  // ISO date string
  customer_created_before?: string; // ISO date string
}

export interface NotificationList {
  id: string;
  name: string;
  description?: string;
  list_type: NotificationListType;
  filter_criteria: FilterCriteria;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface NotificationTemplate {
  id: string;
  name: string;
  channel: NotificationChannel;
  subject?: string;
  body: string;
  is_active: boolean;
  builder_document?: Record<string, unknown>;  // EmailDocument JSON for visual builder
  created_at: string;
  updated_at: string;
}

export interface NotificationCampaign {
  id: string;
  name?: string;
  channel: NotificationChannel;
  subject?: string;
  body: string;
  status: CampaignStatus;
  total_recipients: number;
  sent_count: number;
  failed_count: number;
  opted_out_count: number;
  open_count: number;
  click_count: number;
  open_rate: number;
  click_rate: number;
  list_name?: string;
  template_name?: string;
  sent_by_user_name?: string;
  created_at: string;
  completed_at?: string;
}

export interface PreviewRecipient {
  id: string;
  name: string;
  email?: string;
  phone?: string;
  notifications_enabled: boolean;
}

export interface ListPreview {
  total_matching: number;
  eligible_count: number;
  opted_out_count: number;
  sample_recipients: PreviewRecipient[];
}

export interface TemplateVariables {
  variables: Record<string, string>;
}

// ============================================
// Request Types
// ============================================

export interface ListCreateRequest {
  name: string;
  description?: string;
  list_type: NotificationListType;
  filter_criteria: FilterCriteria;
}

export interface ListUpdateRequest {
  name?: string;
  description?: string;
  list_type?: NotificationListType;
  filter_criteria?: FilterCriteria;
  is_active?: boolean;
}

export interface TemplateCreateRequest {
  name: string;
  channel: NotificationChannel;
  subject?: string;
  body: string;
  builder_document?: Record<string, unknown>;  // EmailDocument JSON for visual builder
}

export interface TemplateUpdateRequest {
  name?: string;
  channel?: NotificationChannel;
  subject?: string;
  body?: string;
  is_active?: boolean;
  builder_document?: Record<string, unknown>;  // EmailDocument JSON for visual builder
}

export interface CampaignSendRequest {
  list_id?: string;
  template_id?: string;
  name?: string;
  channel: NotificationChannel;
  subject?: string;
  body: string;
}

// ============================================
// Helper Functions
// ============================================

function getAuthHeaders(): HeadersInit {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || `Request failed with status ${response.status}`);
  }
  
  // Handle 204 No Content
  if (response.status === 204) {
    return undefined as T;
  }
  
  return response.json();
}

// ============================================
// Lists API
// ============================================

export async function getLists(): Promise<NotificationList[]> {
  const response = await fetch(`${API_BASE_URL}/api/v1/notifications/lists`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  return handleResponse<NotificationList[]>(response);
}

export async function getList(listId: string): Promise<NotificationList> {
  const response = await fetch(`${API_BASE_URL}/api/v1/notifications/lists/${listId}`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  return handleResponse<NotificationList>(response);
}

export async function createList(data: ListCreateRequest): Promise<NotificationList> {
  const response = await fetch(`${API_BASE_URL}/api/v1/notifications/lists`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(data),
  });
  return handleResponse<NotificationList>(response);
}

export async function updateList(listId: string, data: ListUpdateRequest): Promise<NotificationList> {
  const response = await fetch(`${API_BASE_URL}/api/v1/notifications/lists/${listId}`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify(data),
  });
  return handleResponse<NotificationList>(response);
}

export async function deleteList(listId: string): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/api/v1/notifications/lists/${listId}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });
  return handleResponse<void>(response);
}

export async function previewList(listId: string, channel: NotificationChannel): Promise<ListPreview> {
  const response = await fetch(
    `${API_BASE_URL}/api/v1/notifications/lists/${listId}/preview?channel=${channel}`,
    {
      method: 'GET',
      headers: getAuthHeaders(),
    }
  );
  return handleResponse<ListPreview>(response);
}

// ============================================
// Templates API
// ============================================

export async function getTemplates(): Promise<NotificationTemplate[]> {
  const response = await fetch(`${API_BASE_URL}/api/v1/notifications/templates`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  return handleResponse<NotificationTemplate[]>(response);
}

export async function createTemplate(data: TemplateCreateRequest): Promise<NotificationTemplate> {
  const response = await fetch(`${API_BASE_URL}/api/v1/notifications/templates`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(data),
  });
  return handleResponse<NotificationTemplate>(response);
}

export async function updateTemplate(templateId: string, data: TemplateUpdateRequest): Promise<NotificationTemplate> {
  const response = await fetch(`${API_BASE_URL}/api/v1/notifications/templates/${templateId}`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify(data),
  });
  return handleResponse<NotificationTemplate>(response);
}

export async function deleteTemplate(templateId: string): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/api/v1/notifications/templates/${templateId}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });
  return handleResponse<void>(response);
}

export async function getTemplateVariables(): Promise<TemplateVariables> {
  const response = await fetch(`${API_BASE_URL}/api/v1/notifications/templates/variables`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  return handleResponse<TemplateVariables>(response);
}

// ============================================
// Campaigns API
// ============================================

export async function getCampaigns(limit: number = 50, offset: number = 0): Promise<NotificationCampaign[]> {
  const response = await fetch(
    `${API_BASE_URL}/api/v1/notifications/campaigns?limit=${limit}&offset=${offset}`,
    {
      method: 'GET',
      headers: getAuthHeaders(),
    }
  );
  return handleResponse<NotificationCampaign[]>(response);
}

export async function getCampaign(campaignId: string): Promise<NotificationCampaign> {
  const response = await fetch(`${API_BASE_URL}/api/v1/notifications/campaigns/${campaignId}`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  return handleResponse<NotificationCampaign>(response);
}

export async function sendCampaign(data: CampaignSendRequest): Promise<NotificationCampaign> {
  const response = await fetch(`${API_BASE_URL}/api/v1/notifications/campaigns/send`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(data),
  });
  return handleResponse<NotificationCampaign>(response);
}

export async function previewCampaignRecipients(
  channel: NotificationChannel,
  listId?: string
): Promise<ListPreview> {
  let url = `${API_BASE_URL}/api/v1/notifications/campaigns/preview?channel=${channel}`;
  if (listId) {
    url += `&list_id=${listId}`;
  }
  
  const response = await fetch(url, {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  return handleResponse<ListPreview>(response);
}


// ============================================
// Email Image Upload
// ============================================

export interface EmailImageUploadResponse {
  upload_url: string;
  fields: Record<string, string>;
  s3_key: string;
  public_url: string;
  expires_in: number;
}

export interface EmailImageCompleteResponse {
  public_url: string;
  s3_key: string;
}

export async function initiateEmailImageUpload(
  filename: string,
  contentType: string = 'image/jpeg'
): Promise<EmailImageUploadResponse> {
  const response = await fetch(`${API_BASE_URL}/api/v1/notifications/email-images/upload`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ filename, content_type: contentType }),
  });
  return handleResponse<EmailImageUploadResponse>(response);
}

export async function uploadEmailImage(file: File): Promise<string> {
  // Step 1: Get presigned URL
  const uploadData = await initiateEmailImageUpload(file.name, file.type);
  
  // Step 2: Upload to S3
  const formData = new FormData();
  Object.entries(uploadData.fields).forEach(([key, value]) => {
    formData.append(key, value);
  });
  formData.append('file', file);
  
  const uploadResponse = await fetch(uploadData.upload_url, {
    method: 'POST',
    body: formData,
  });
  
  if (!uploadResponse.ok) {
    throw new Error('Failed to upload image to storage');
  }
  
  // Step 3: Return the public URL
  return uploadData.public_url;
}

export async function completeEmailImageUpload(
  s3Key: string,
  filename: string
): Promise<EmailImageCompleteResponse> {
  const response = await fetch(`${API_BASE_URL}/api/v1/notifications/email-images/complete`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ s3_key: s3Key, filename }),
  });
  return handleResponse<EmailImageCompleteResponse>(response);
}

// ============================================
// Utility Functions
// ============================================

export function getChannelLabel(channel: NotificationChannel): string {
  switch (channel) {
    case 'sms':
      return 'SMS';
    case 'email_plain':
      return 'Plain Text Email';
    case 'email_html':
      return 'HTML Email';
    default:
      return channel;
  }
}

export function getStatusLabel(status: CampaignStatus): string {
  switch (status) {
    case 'pending':
      return 'Pending';
    case 'sending':
      return 'Sending...';
    case 'completed':
      return 'Completed';
    case 'failed':
      return 'Failed';
    default:
      return status;
  }
}

export function getStatusColor(status: CampaignStatus): string {
  switch (status) {
    case 'pending':
      return 'text-yellow-600 bg-yellow-100';
    case 'sending':
      return 'text-blue-600 bg-blue-100';
    case 'completed':
      return 'text-green-600 bg-green-100';
    case 'failed':
      return 'text-red-600 bg-red-100';
    default:
      return 'text-gray-600 bg-gray-100';
  }
}

